/* eslint-disable react/react-in-jsx-scope */
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

import { AppContext } from "../../AppContext";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Typography from "@mui/material/Typography";

import Assessment from "@mui/icons-material/Assessment";
import Schema from "@mui/icons-material/Schema";

import DataLoading from "./../DataLoading";
import DashboardWebApi, { LoginStatus } from "../../services/DashboardWebApi";

const Home = () => {
  const logHeader = "[Home]";
  const isAuthenticated = DashboardWebApi.IsAuthenticated();
  const loginUserStatus: LoginStatus = DashboardWebApi.GetLoginStatus();

  const navigate = useNavigate();
  const { embeddingData } = useContext(AppContext);

  console.debug(`${logHeader}`, { isAuthenticated });
  console.debug(`${logHeader}`, { loginUserStatus });
  console.debug(`${logHeader}`, { embeddingData });

  /**
  if (loginUserStatus == "Pending" && !isAuthenticated) {
    return (
      <Container maxWidth={false}>
        <Alert sx={{ border: 1, padding: 2, mx: 2, marginTop: 5 }} severity='info' >Click the LOGIN button in the upper right to get started.</Alert>
      </Container>
    )
  }
  */

  if (isAuthenticated && loginUserStatus === "Failed" && !embeddingData.user) {
    return (
      <Container maxWidth={false}>
        <Alert
          sx={{ border: 1, padding: 2, mx: 2, marginTop: 5 }}
          severity="info"
          data-testid="home-login-failed-alert"
        >
          Your account may not have been registered for dashboards. Please
          contact your adiministrator.
        </Alert>
      </Container>
    );
  }

  if (
    isAuthenticated &&
    embeddingData.user &&
    loginUserStatus === "Success" &&
    !embeddingData.tenantName
  ) {
    return (
      <Container maxWidth={false}>
        <Alert
          sx={{ border: 1, padding: 2, mx: 2, marginTop: 5 }}
          severity="info"
          data-testid="home-no-tenant-alert"
        >
          Your account may not have been assigned to a tenant. Please contact
          your adiministrator.
        </Alert>
      </Container>
    );
  }

  if (
    isAuthenticated &&
    loginUserStatus === "Success" &&
    embeddingData.workspaceArtifactsLoading
  ) {
    return <DataLoading />;
  } else if (
    isAuthenticated &&
    loginUserStatus === "Success" &&
    embeddingData.user &&
    embeddingData.tenantName
  ) {
    return (
      <Container maxWidth={false}>
        <Container maxWidth="xl" data-testid="home-page-container">
          <Alert sx={{ border: 1, marginTop: 5 }} severity="info" data-testid="home-welcome-message">
            Now that you have logged in, you can use the left navigation menu to
            navigate to the reports accessible within this tenant.
          </Alert>

          <Typography variant="h6" component="h4" sx={{ mt: 2, mb: "6px" }} data-testid="home-session-info-header">
            Login Session Info:
          </Typography>

          <TableContainer
            component={Paper}
            sx={{ border: 1, backgroundColor: "#EEEEEE" }}
            data-testid="home-session-info-table"
          >
            <Table
              sx={{ minWidth: 650 }}
              aria-label="simple table"
              size="small"
            >
              <TableBody>
                <TableRow key={"username"}>
                  <TableCell scope="row" sx={{ width: "200px" }}>
                    User Login:
                  </TableCell>
                  <TableCell data-testid="home-user-login">
                    <strong>{embeddingData.loginId}</strong>
                  </TableCell>
                </TableRow>
                <TableRow key={"name"}>
                  <TableCell scope="row" sx={{ width: "200px" }}>
                    User Display Name:
                  </TableCell>
                  <TableCell data-testid="home-user-display-name">
                    <strong>{embeddingData.user}</strong>
                  </TableCell>
                </TableRow>
                <TableRow key={"tenant"}>
                  <TableCell scope="row" sx={{ width: "200px" }}>
                    Tenant Name:
                  </TableCell>
                  <TableCell data-testid="home-tenant-name">
                    <strong>{embeddingData.tenantName}</strong>
                  </TableCell>
                </TableRow>
                <TableRow key={"userCanEdit"}>
                  <TableCell scope="row">User can edit content:</TableCell>
                  <TableCell data-testid="home-user-can-edit">
                    <strong>{String(embeddingData.userCanEdit)}</strong>
                  </TableCell>
                </TableRow>
                <TableRow key={"userCanCreate"}>
                  <TableCell scope="row">User can create content:</TableCell>
                  <TableCell data-testid="home-user-can-create">
                    <strong>{String(embeddingData.userCanCreate)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography
            variant="h6"
            component="h4"
            sx={{ mt: 2, borderBottom: 1, mb: "8px" }}
            data-testid="home-tenant-contents-header"
          >
            Tenant Contents
          </Typography>

          <Grid container>
            <Grid size={6} sx={{ pr: "4px" }}>
              <Box
                sx={{
                  fontSize: "1.25rem",
                  color: "white",
                  background:
                    "linear-gradient(to bottom, #607D8B, #455A64, #607D8B )",
                  padding: "4px",
                  paddingLeft: "12px",
                  borderTopLeftRadius: "4px",
                  borderTopRightRadius: "4px",
                }}
                data-testid="home-reports-header"
              >
                Reports
              </Box>
              <List
                sx={{
                  backgroundColor: "#EEEEEE",
                  border: "1px solid #CCCCCC",
                  pb: 0,
                }}
                data-testid="home-reports-list"
              >
                {embeddingData.reports?.map((report) => (
                  <ListItem key={report.id} divider dense>
                    <ListItemButton
                      data-testid={`home-report-${report.id}`}
                      onClick={() => {
                        localStorage.setItem(
                          "defaultreport",
                          `/reports/${report.id}`,
                        );
                        navigate(`/reports/${report.id}`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: "#607D8B" }}>
                          <Assessment />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={report.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid size={6} sx={{ pl: "4px" }}>
              <Box
                sx={{
                  fontSize: "1.25rem",
                  color: "white",
                  background:
                    "linear-gradient(to bottom, #607D8B, #455A64, #607D8B )",
                  padding: "4px",
                  paddingLeft: "12px",
                  borderTopLeftRadius: "4px",
                  borderTopRightRadius: "4px",
                }}
                data-testid="home-datasets-header"
              >
                Datasets
              </Box>
              <List
                sx={{
                  backgroundColor: "#EEEEEE",
                  border: "1px solid #CCCCCC",
                  pb: 0,
                }}
                data-testid="home-datasets-list"
              >
                {embeddingData.datasets?.map((dataset) => (
                  <ListItem key={dataset.id} divider dense>
                    <ListItemButton
                      data-testid={`home-dataset-${dataset.id}`}
                      onClick={() => {
                        navigate(`/reports/${dataset.id}`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: "#607D8B" }}>
                          <Schema />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={dataset.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Container>
    );
  }
};

export default Home;
